import { StopIcon } from "@heroicons/react/24/solid";
import { DangerButton } from "components/ui/danger-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import { formatElapsed, useTimer } from "contexts/timer-context";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useWindowSize } from "react-use";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";

export const FloatingTimer: React.FC = () => {
    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();
    const {
        timerState,
        elapsedSeconds,
        clientId,
        clientName,
        stopTimer,
        resetTimer,
        saveEntry,
    } = useTimer();
    const { width: windowWidth, height: windowHeight } = useWindowSize();
    const dragControls = useDragControls();
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [formAbove, setFormAbove] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (timerState === "stopped" && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setFormAbove(windowHeight - rect.bottom < 200);
        }
    }, [timerState, windowHeight]);

    const handleSave = useCallback(async () => {
        if (!note.trim()) return;
        setSaving(true);
        try {
            await saveEntry(note);
            setNote("");
        } catch {
            await fancyAlert("Error", "Failed to save time entry. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [note, saveEntry]);

    if (!trackTimeFeature || !settings.trackTimeEnabled) return null;

    const isVisible = timerState !== "idle";

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    key="floating-timer"
                    ref={containerRef}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    drag
                    dragControls={dragControls}
                    dragListener={false}
                    dragElastic={0}
                    dragMomentum={false}
                    dragConstraints={{
                        top: 0,
                        bottom: windowHeight - 120,
                        left: -(windowWidth - 320),
                        right: 0,
                    }}
                    className="fixed top-4 right-4 z-1400">
                    <div className="relative min-w-[280px]">
                        <div
                            className="bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3"
                            onPointerDown={e => dragControls.start(e)}>
                            <div className="flex items-center justify-between gap-3 cursor-grab active:cursor-grabbing">
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/clients/show/${clientId}`}
                                        className="text-sm font-medium text-brand-600 hover:text-brand-800 truncate block">
                                        {clientName}
                                    </Link>
                                    <div className="text-lg font-mono font-semibold text-gray-900">
                                        {formatElapsed(elapsedSeconds)}
                                    </div>
                                </div>
                                {timerState === "running" && (
                                    <DangerButton onClick={stopTimer} title="Stop timer">
                                        <StopIcon className="h-5 w-5" />
                                    </DangerButton>
                                )}
                            </div>
                        </div>

                        {timerState === "stopped" && (
                            <div
                                className={`absolute left-0 right-0 ${formAbove ? "bottom-full mb-2" : "top-full mt-2"}`}>
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3 space-y-2">
                                    <TextArea
                                        textSize="sm"
                                        rows={2}
                                        value={note}
                                        onChange={e => setNote(e.target.value)}
                                        placeholder="What was done..."
                                    />
                                    <div className="flex gap-2">
                                        <PrimaryButton
                                            onClick={handleSave}
                                            disabled={!note.trim() || saving}>
                                            {saving ? "Saving..." : "Save"}
                                        </PrimaryButton>
                                        <SecondaryButton
                                            onClick={() => {
                                                setNote("");
                                                resetTimer();
                                            }}>
                                            Discard
                                        </SecondaryButton>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
